interface NodeIconProps {
  icon: string | null | undefined
  fallback?: React.ReactNode
  className?: string
  size?: 'sm' | 'md' | 'lg' | 'xl'
}

/**
 * NodeIcon component that renders either an SVG image (if icon is a URL)
 * or an emoji/text (if icon is not a URL).
 *
 * Used by canvas nodes (TriggerNode, ActionNode) and NodePropertiesPanel.
 */
export function NodeIcon({ icon, fallback, className = '', size = 'md' }: NodeIconProps) {
  // Size classes for different contexts
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-10 h-10',
  }

  const textSizeClasses = {
    sm: 'text-sm',
    md: 'text-lg',
    lg: 'text-xl',
    xl: 'text-2xl',
  }

  // If no icon, show fallback
  if (!icon) {
    return <>{fallback}</> || null
  }

  // Check if icon is a URL
  const isUrl = icon.startsWith('http://') || icon.startsWith('https://')

  if (isUrl) {
    return (
      <img
        src={icon}
        alt="Node icon"
        className={`object-contain ${sizeClasses[size]} ${className}`}
        onError={(e) => {
          // On error, hide the image and show fallback if possible
          const target = e.target as HTMLImageElement
          target.style.display = 'none'
          // If parent has a fallback slot, it will be shown
        }}
      />
    )
  }

  // Render as emoji/text
  return (
    <span className={`${textSizeClasses[size]} ${className}`}>
      {icon}
    </span>
  )
}

export default NodeIcon
